import React from 'react';
import {
  Box,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudIcon from '@mui/icons-material/Cloud';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import type { McpToolMetadata } from '../../contexts/PlannerContext';

interface McpBadgeProps {
  source: string;
  metadata: McpToolMetadata;
  index: number;
}

const getCategoryIcon = (category: string) => {
  switch (category.toLowerCase()) {
    case 'search':
      return <SearchIcon sx={{ fontSize: 14 }} />;
    case 'weather':
      return <CloudIcon sx={{ fontSize: 14 }} />;
    case 'destination':
      return <LocationOnIcon sx={{ fontSize: 14 }} />;
    default:
      return <HelpOutlineIcon sx={{ fontSize: 14 }} />;
  }
};

export const McpBadge: React.FC<McpBadgeProps> = ({ source, metadata, index }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const theme = useTheme();
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const handleClick = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  const handleOpenDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsTooltipOpen(false);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const tooltipContent = (
    <Box sx={{ maxWidth: 400, p: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: '#ff6b9d',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {metadata.tool_name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: alpha('#fff', 0.6),
            fontSize: '0.65rem',
          }}
        >
          {metadata.category}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{
          color: alpha('#fff', 0.9),
          fontSize: '0.8rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {metadata.preview}
      </Typography>

      {metadata.full_output.length > 300 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: '#ff6b9d',
            cursor: 'pointer',
            '&:hover': {
              color: '#ffa6c1',
            },
          }}
          onClick={handleOpenDialog}
        >
          View full response →
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        open={isTooltipOpen}
        onClose={() => setIsTooltipOpen(false)}
        disableFocusListener
        disableHoverListener={false}
        disableTouchListener={false}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              borderRadius: 2,
              maxWidth: 'none',
              p: 0,
            },
          },
          arrow: {
            sx: {
              color: '#1a1a1a',
            },
          },
        }}
      >
        <Box
          onClick={handleClick}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.75,
            py: 0.75,
            mx: 0.5,
            borderRadius: 999,
            background: 'linear-gradient(135deg, #ff6b9d 0%, #ffa6c1 100%)',
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(255, 107, 157, 0.3)',
            verticalAlign: 'middle',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(255, 107, 157, 0.4)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          {getCategoryIcon(metadata.category)}
          <span>{source} mcp used</span>
        </Box>
      </Tooltip>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
            borderBottom: `1px solid ${alpha('#000', 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {getCategoryIcon(metadata.category)}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff6b9d' }}>
                {metadata.tool_name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {metadata.category} tool response
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: alpha('#000', 0.05),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              backgroundColor: alpha('#f8f9fa', 0.5),
              borderRadius: 2,
              maxHeight: '60vh',
              overflow: 'auto',
            }}
          >
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                color: 'text.primary',
              }}
            >
              {metadata.full_output}
            </Typography>
          </Paper>
        </DialogContent>
      </Dialog>
    </>
  );
};
